import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { getCurrentAuthenticatedUser } from '@tmx/auth';
import { accountsEnvironment } from '@tmx/data-access';
import { from, map, Subject, Subscription } from 'rxjs';

import { OrderNotificationEvent } from '../models/order-notification-event';
import { PrimaryNavKey } from '../models/primary-nav-key';
import { userLoggedOut$ } from '../repositories/account-manager/account-manager.repository';
import { primaryNavOptions$ } from '../repositories/ams/ams.repository';
import { reportToTrackjs } from '../utils/report-to-trackjs';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private connection: null | signalR.HubConnection = null;
  private connectionSubscription: null | Subscription = null;

  receiveOrderNotificationEvent$ = new Subject<OrderNotificationEvent>();

  constructor() {
    // No unsubscribe because we want to listen for entire app lifetime.
    userLoggedOut$.subscribe(() => {
      this.stopSignalR();
    });

    // No unsubscribe because we want to listen for entire app lifetime.
    primaryNavOptions$
      .pipe(map((options) => options.get(PrimaryNavKey.Accounts) !== null))
      .subscribe((isViewingChildAccount) => {
        if (isViewingChildAccount) {
          this.startSignalR();
        } else {
          this.stopSignalR();
        }
      });
  }

  private startSignalR() {
    this.stopSignalR();

    const authToken = getCurrentAuthenticatedUser().authToken;
    const url = `${accountsEnvironment.baseUrl}/api/v1/messages?authToken=${authToken}`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.connection.on(
      'ReceiveOrderNotification',
      (event: OrderNotificationEvent) => {
        this.receiveOrderNotificationEvent$.next(event);
      }
    );

    this.connectionSubscription = from(this.connection.start()).subscribe({
      error: () => {
        reportToTrackjs(
          'notification repository',
          'SignalR could not connect to hub.'
        );
      },
    });
  }

  private stopSignalR() {
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = null;
    }

    if (this.connection) {
      this.connection.off('ReceiveOrderNotification');
      this.connection.stop();
      this.connection = null;
    }
  }
}
